import { isEmpty } from 'fp-ts/Record';
import { useEffect } from 'react';
import { Button, Stack } from '@optimus-web/core';
import { lt, useFormControls, createZodFormResolver } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useConfirmationDialog } from '@mews-commander/ui';
import { ownerTypes } from '../../constants';
import { OwnerTypeFilter } from '../../../common/filters';
import { AgeingFilter } from '../Filters/AgeingFilter';
import { OwnerFilter } from '../Filters/OwnerFilter';
import { LedgersExportPanel } from '../../../common/export/LedgersExportPanel';
import { useCityLedgerFilterContext, useCityLedgerSelectedDate } from '../../context/CityLedgerFilterContext';
import { FormatFilter } from '../Filters/FormatFilter';
import { getOwnerType } from '../../utils/getOwnerType';
import { useCityLedgerExportMutation } from '../../hooks/useCityLedgerExportMutation';
import { getAgeingType } from '../../utils/getAgeingType';
import { LedgersDateField } from '../../../common/LedgersDateField';
import { exportFiltersSchema, ExportValues, CityLedgerExportField } from './schemaExport/exportSchema';
import { defaultExportValues } from './schemaExport/defaultExportValues';

interface CityLedgerExportDialogProps {
    onClose: () => void;
    type: 'export' | 'print';
}

const getButtonText = (type: 'export' | 'print') => {
    switch (type) {
        case 'export':
            return lt('Export');
        case 'print':
            return lt('Print');
    }
};

export const CityLedgerExportDialog = ({ onClose, type }: CityLedgerExportDialogProps) => {
    const { filterValues } = useCityLedgerFilterContext();
    const { selectedDate } = useCityLedgerSelectedDate();
    const enterpriseId = useCurrentEnterpriseId();
    const { confirmationDialog, openConfirmationDialog } = useConfirmationDialog({
        onConfirm: () => onClose?.(),
    });

    const { mutate: exportCityLedgerReport, error, isPending } = useCityLedgerExportMutation(enterpriseId);

    const handleSubmit = (values: ExportValues) => {
        const { ownerTypes: owner, ageing: ageingTypes, date, ...rest } = values;
        const ownerTypesArray = getOwnerType(owner ?? []);

        exportCityLedgerReport({
            ...rest,
            localDateOnly: date,
            ownerTypesArray,
            ageing: getAgeingType(ageingTypes ?? []),
        });
        onClose();
    };

    const {
        renderFormContent: renderExportForm,
        submit: submitExportForm,
        errors,
        hasDirtyFields,
        watch,
        setValue,
    } = useFormControls<ExportValues>({
        id: 'city-ledger-export',
        defaultValues: {
            ...defaultExportValues,
            ...filterValues,
            date: selectedDate,
        },
        resolver: createZodFormResolver(exportFiltersSchema),
        mode: 'onSubmit',
        onSubmit: handleSubmit,
    });

    const handleClose = () => {
        if (hasDirtyFields) {
            openConfirmationDialog();
        } else {
            onClose?.();
        }
    };

    const owner = watch ? watch(CityLedgerExportField.owner) : null;

    // Reset OwnerTypeFilter when owner changes
    useEffect(() => {
        if (owner) {
            setValue(CityLedgerExportField.ownerTypes, defaultExportValues.ownerTypes);
        }
    }, [owner, setValue]);

    return renderExportForm(
        <LedgersExportPanel
            data-test-id="city-ledger-export-panel"
            title={lt('CityLedgerExport')}
            onClose={handleClose}
            submitButton={
                <Button
                    data-test-id="submit-button"
                    type="submit"
                    onClick={submitExportForm}
                    size="medium"
                    variant="primary"
                    loading={!!isPending}
                    disabled={!!error || (errors && !isEmpty(errors))}
                >
                    {getButtonText(type)}
                </Button>
            }
        >
            <Stack spacing="100" vertical>
                <FormatFilter name={CityLedgerExportField.format} />
                <LedgersDateField name={CityLedgerExportField.date} label={lt('Date')} />
                <OwnerFilter name={CityLedgerExportField.owner} />
                <OwnerTypeFilter
                    name={CityLedgerExportField.ownerTypes}
                    ownerTypesOptions={ownerTypes}
                    isDisabled={!!owner}
                />

                <AgeingFilter name={CityLedgerExportField.ageing} />
            </Stack>
            {confirmationDialog}
        </LedgersExportPanel>
    );
};
